/**
 * AI Prompt Enhancement Service
 * 
 * This service enhances AI prompts with rich contextual information
 * while respecting token limits and preserving carefully crafted prompts.
 * 
 * Key Features:
 * - Context-aware prompt enhancement
 * - Token limit management
 * - Historical trend integration
 * - Strategic insight generation
 * - Performance pattern analysis
 */

class AIPromptEnhancer {
  constructor() {
    this.maxContextTokens = 1500; // Reserve tokens for context
    this.maxTotalTokens = 3500; // Total prompt limit
  }

  /**
   * Enhance prompts with historical context while preserving original prompts
   * @param {string} originalPrompt - The carefully crafted original prompt
   * @param {Object} aiContext - Rich context from AI Context Service
   * @param {Object} currentTestData - Current test performance data
   * @param {string} promptType - Type of prompt (summary, nextSteps, insights)
   * @returns {string} Enhanced prompt with context
   */
  enhancePrompt(originalPrompt, aiContext, currentTestData, promptType = 'insights') {
    console.log('🚀 PromptEnhancer: Enhancing prompt with AI context');

    try {
      // Preserve the original prompt structure
      let enhancedPrompt = originalPrompt;

      // Add context section based on prompt type
      const contextSection = this.generateContextSection(aiContext, currentTestData, promptType);
      
      // Intelligently insert context without breaking original prompt structure
      if (contextSection && contextSection.length > 0) {
        // Find the best insertion point
        const insertionPoint = this.findOptimalInsertionPoint(originalPrompt, promptType);
        
        if (insertionPoint !== -1) {
          // Insert context at the optimal point
          enhancedPrompt = 
            originalPrompt.substring(0, insertionPoint) + 
            '\n\n' + contextSection + '\n\n' + 
            originalPrompt.substring(insertionPoint);
        } else {
          // Append context if no optimal insertion point found
          enhancedPrompt = contextSection + '\n\n' + originalPrompt;
        }
      }

      // Ensure prompt doesn't exceed token limits
      enhancedPrompt = this.truncateIfNeeded(enhancedPrompt);

      console.log('✅ PromptEnhancer: Prompt enhanced successfully');
      return enhancedPrompt;

    } catch (error) {
      console.error('❌ PromptEnhancer: Error enhancing prompt:', error);
      return originalPrompt; // Return original prompt as fallback
    }
  }

  /**
   * Generate context section based on AI context and test data
   */
  generateContextSection(aiContext, currentTestData, promptType) {
    if (!aiContext || aiContext.currentTest.isFirst) {
      return "FIRST TEST ANALYSIS - No historical context available. Focus on baseline establishment.";
    }

    let contextSection = `HISTORICAL PERFORMANCE CONTEXT:\n`;
    
    // Add test sequence information
    contextSection += `• This is test ${aiContext.currentTest.position} in the student's SIMCAT journey\n`;
    contextSection += `• Historical data available from ${aiContext.historical.testCount} previous tests\n\n`;

    // Add trend analysis
    if (aiContext.historical.trends && aiContext.historical.trends.status !== 'insufficient_data') {
      contextSection += `PERFORMANCE TRENDS:\n`;
      contextSection += `• Overall trajectory: ${aiContext.historical.trends.overall}\n`;
      
      if (aiContext.historical.trends.sections) {
        Object.entries(aiContext.historical.trends.sections).forEach(([section, trend]) => {
          contextSection += `• ${section} trend: ${trend.percentile}\n`;
        });
      }
      contextSection += '\n';
    }

    // Add pattern insights
    if (aiContext.historical.patterns) {
      contextSection += `STRATEGIC PATTERNS:\n`;
      
      if (aiContext.historical.patterns.strongestSection) {
        contextSection += `• Consistently strongest: ${aiContext.historical.patterns.strongestSection}\n`;
      }
      
      if (aiContext.historical.patterns.weakestSection) {
        contextSection += `• Needs attention: ${aiContext.historical.patterns.weakestSection}\n`;
      }
      
      if (aiContext.historical.patterns.mostImproved) {
        contextSection += `• Most improved: ${aiContext.historical.patterns.mostImproved}\n`;
      }
      
      contextSection += '\n';
    }

    // Add cutoff clearing patterns
    if (aiContext.historical.patterns.consistentCutoffs) {
      contextSection += `CUTOFF CLEARING HISTORY:\n`;
      Object.entries(aiContext.historical.patterns.consistentCutoffs).forEach(([section, rate]) => {
        const performance = rate >= 75 ? 'Excellent' : rate >= 50 ? 'Good' : rate >= 25 ? 'Inconsistent' : 'Struggling';
        contextSection += `• ${section}: ${rate.toFixed(0)}% success rate (${performance})\n`;
      });
      contextSection += '\n';
    }

    // Add progression insights
    if (aiContext.historical.progression) {
      contextSection += `LEARNING PROGRESSION:\n`;
      
      if (aiContext.historical.progression.overallGrowth) {
        const growthDirection = aiContext.historical.progression.overallGrowth > 2 ? 'Strong upward' : 
                              aiContext.historical.progression.overallGrowth > 0 ? 'Gradual upward' :
                              aiContext.historical.progression.overallGrowth < -2 ? 'Concerning downward' : 'Stable';
        contextSection += `• Growth trajectory: ${growthDirection}\n`;
      }
      
      if (aiContext.historical.progression.consistencyScore) {
        const consistency = aiContext.historical.progression.consistencyScore > 80 ? 'Highly consistent' :
                           aiContext.historical.progression.consistencyScore > 60 ? 'Moderately consistent' : 'Variable';
        contextSection += `• Performance consistency: ${consistency}\n`;
      }
      
      contextSection += '\n';
    }

    // Add context-specific insights based on prompt type
    if (promptType === 'nextSteps' && aiContext.historical.progression.improvementAreas) {
      contextSection += `PRIORITY IMPROVEMENT AREAS:\n`;
      aiContext.historical.progression.improvementAreas.forEach(area => {
        contextSection += `• ${area.area}: ${area.issue} (${area.severity} priority)\n`;
      });
      contextSection += '\n';
    }

    return contextSection;
  }

  /**
   * Find optimal insertion point in original prompt
   */
  findOptimalInsertionPoint(originalPrompt, promptType) {
    // Look for common insertion markers
    const insertionMarkers = [
      'Based on the provided data',
      'Using the student data',
      'Analyze the following',
      'Generate insights',
      'Create recommendations',
      'Based on the performance data'
    ];

    for (const marker of insertionMarkers) {
      const index = originalPrompt.indexOf(marker);
      if (index !== -1) {
        return index;
      }
    }

    // Look for prompt type specific markers
    if (promptType === 'summary') {
      const summaryMarkers = ['Generate', 'Provide', 'Create'];
      for (const marker of summaryMarkers) {
        const index = originalPrompt.indexOf(marker);
        if (index !== -1) {
          return index;
        }
      }
    }

    return -1; // No suitable insertion point found
  }

  /**
   * Truncate prompt if it exceeds token limits
   */
  truncateIfNeeded(prompt) {
    // Rough estimation: 1 token ≈ 4 characters
    const estimatedTokens = prompt.length / 4;
    
    if (estimatedTokens <= this.maxTotalTokens) {
      return prompt;
    }

    console.warn(`⚠️ PromptEnhancer: Prompt too long (${estimatedTokens} tokens), truncating...`);
    
    // Truncate context section while preserving original prompt
    const lines = prompt.split('\n');
    const contextStartIndex = lines.findIndex(line => line.includes('HISTORICAL PERFORMANCE CONTEXT'));
    const contextEndIndex = lines.findIndex((line, index) => 
      index > contextStartIndex && 
      (line.includes('Based on') || line.includes('Generate') || line.includes('Analyze'))
    );

    if (contextStartIndex !== -1 && contextEndIndex !== -1) {
      // Remove some context lines to fit within limits
      const contextLines = lines.slice(contextStartIndex, contextEndIndex);
      const originalPromptLines = lines.slice(contextEndIndex);
      
      // Keep most important context (first few sections)
      const priorityContextLines = contextLines.slice(0, Math.min(20, contextLines.length));
      
      const truncatedPrompt = [...priorityContextLines, ...originalPromptLines].join('\n');
      
      console.log(`✂️ PromptEnhancer: Truncated to fit token limits`);
      return truncatedPrompt;
    }

    // Fallback: Simple truncation
    return prompt.substring(0, this.maxTotalTokens * 4);
  }

  /**
   * Generate context summary for token-constrained environments
   */
  generateCompactContext(aiContext) {
    if (!aiContext || aiContext.currentTest.isFirst) {
      return "First test - baseline establishment";
    }

    let compact = `Test ${aiContext.currentTest.position}`;
    
    if (aiContext.historical.trends.overall) {
      compact += ` | Overall: ${aiContext.historical.trends.overall}`;
    }
    
    if (aiContext.historical.patterns.strongestSection && aiContext.historical.patterns.weakestSection) {
      compact += ` | Strong: ${aiContext.historical.patterns.strongestSection}, Weak: ${aiContext.historical.patterns.weakestSection}`;
    }

    return compact;
  }

  /**
   * Validate prompt enhancement
   */
  validateEnhancement(originalPrompt, enhancedPrompt) {
    const validation = {
      success: true,
      issues: [],
      metrics: {
        originalLength: originalPrompt.length,
        enhancedLength: enhancedPrompt.length,
        contextAdded: enhancedPrompt.length - originalPrompt.length,
        estimatedTokens: enhancedPrompt.length / 4
      }
    };

    // Check if original prompt structure is preserved
    if (!enhancedPrompt.includes(originalPrompt.substring(0, 100))) {
      validation.issues.push('Original prompt structure may be compromised');
    }

    // Check token limits
    if (validation.metrics.estimatedTokens > this.maxTotalTokens) {
      validation.issues.push('Enhanced prompt exceeds token limits');
    }

    // Check if context was successfully added
    if (validation.metrics.contextAdded < 50) {
      validation.issues.push('Minimal context added');
    }

    validation.success = validation.issues.length === 0;

    return validation;
  }
}

export default new AIPromptEnhancer();